<template>
  <v-card>
    <v-card-title class="text-h5 pa-4">
      {{ getDialogTitle }}
    </v-card-title>
    <v-card-text class="pa-4">
      <v-form ref="form" v-model="valid">
        <v-text-field
          v-model="formData.name"
          label="Cruise Name"
          :rules="[v => !!v || 'Cruise name is required']"
          variant="outlined"
          class="mb-4"
          :readonly="isExpired"
        ></v-text-field>
        
        <!-- Removed date fields as they are not modifiable -->
        
        <v-text-field
          v-model="formData.aboutUs"
          label="About Us"
          variant="outlined"
          class="mb-4"
          :readonly="isExpired"
        ></v-text-field>
        
        <v-text-field
          v-model="formData.socialLink"
          label="Social Link"
          variant="outlined"
          class="mb-4"
          :readonly="isExpired"
        ></v-text-field>
      </v-form>
    </v-card-text>
    <v-card-actions class="pa-4">
      <v-spacer></v-spacer>
      <v-btn 
        color="grey-darken-1" 
        variant="text" 
        @click="cancelEdit"
      >
        {{ isExpired ? 'Close' : 'Cancel' }}
      </v-btn>
      <v-btn 
        color="primary" 
        @click="submitForm"
        :loading="loading"
        :disabled="!valid || isExpired"
        v-if="!isExpired"
      >
        Save Changes
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import axios from 'axios';
import { Constant } from '@/config/constants';
import type { DetailedCruise } from '@/interfaces/cruise';

const props = defineProps<{
  cruise: DetailedCruise;
}>();

const emit = defineEmits(['close', 'updated']);

const valid = ref(false);
const loading = ref(false);

// Check if cruise is expired
const isExpired = computed(() => {
  return props.cruise.expired === true;
});

// Determine if the cruise information is missing important data
const isInformationMissing = computed(() => {
  return !props.cruise.aboutUs && !props.cruise.socialLink;
});

// Compute the dialog title based on conditions
const getDialogTitle = computed(() => {
  if (isExpired.value) return 'View Cruise';
  if (isInformationMissing.value) return 'Add Cruise Information';
  return 'Edit Cruise';
});

const formData = reactive({
  id: props.cruise.id,
  name: props.cruise.name || '',
  aboutUs: props.cruise.aboutUs || '',
  socialLink: props.cruise.socialLink || ''
});

const cancelEdit = () => {
  emit('close');
};

const submitForm = async () => {
  if (!valid.value || isExpired.value) {
    return;
  }
  
  try {
    loading.value = true;
    
    // Update to use the correct endpoint format according to API spec
    // The cruiseId should be in the request body as specified
    await axios.put(`${Constant.API_URL}${Constant.MODIFY_CRUISE}`, {
      ...formData,
      cruiseId: formData.id
    });

    
    emit('updated');
  } catch (error) {
    console.error('Failed to update cruise:', error);
  } finally {
    loading.value = false;
  }
};
</script>